import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { Home, ChevronDown, ChevronUp } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

function remainingMortgageBalance(loan: number, mRate: number, nPmts: number, yearsPaid: number): number {
  const pmtsMade = Math.min(yearsPaid, nPmts / 12) * 12
  if (mRate === 0) return Math.max(0, loan * (1 - pmtsMade / nPmts))
  return loan * (Math.pow(1 + mRate, nPmts) - Math.pow(1 + mRate, pmtsMade)) / (Math.pow(1 + mRate, nPmts) - 1)
}

export default function BuyVsRentCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef; currency: string }>()

  // Buying inputs
  const [purchasePriceStr, setPurchasePriceStr] = useState("")
  const [downPaymentPct, setDownPaymentPct] = useState(20)
  const [mortgageRate, setMortgageRate] = useState(5.5)
  const [loanTerm, setLoanTerm] = useState(25)
  const [propertyTaxRateStr, setPropertyTaxRateStr] = useState("1.1")
  const [insuranceStr, setInsuranceStr] = useState("")
  const [hoaStr, setHoaStr] = useState("")
  const [appreciation, setAppreciation] = useState(3)
  const [sellingCosts, setSellingCosts] = useState(6)

  // Renting inputs
  const [monthlyRentStr, setMonthlyRentStr] = useState("")
  const [rentIncrease, setRentIncrease] = useState(3)
  const [renterInsuranceStr, setRenterInsuranceStr] = useState("")

  // Assumption inputs
  const [timeHorizon, setTimeHorizon] = useState(10)
  const [investmentReturn, setInvestmentReturn] = useState(7)

  // UI state
  const [activeTab, setActiveTab] = useState<"buying" | "renting" | "assumptions">("buying")
  const [showYearTable, setShowYearTable] = useState(false)

  const results = useMemo(() => {
    const purchasePrice = parseFloat(purchasePriceStr) || 0
    const monthlyRent = parseFloat(monthlyRentStr) || 0
    if (purchasePrice <= 0 || monthlyRent <= 0) return null

    const downPayment = purchasePrice * downPaymentPct / 100
    const loanAmount = purchasePrice - downPayment
    const monthlyRate = mortgageRate / 100 / 12
    const nPayments = loanTerm * 12
    const monthlyMortgage = monthlyRate > 0
      ? loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, nPayments)) / (Math.pow(1 + monthlyRate, nPayments) - 1)
      : loanAmount / nPayments

    const propertyTaxRate = parseFloat(propertyTaxRateStr) / 100 || 0.011
    const annualInsurance = parseFloat(insuranceStr) || 0
    const monthlyHOA = parseFloat(hoaStr) || 0
    const renterInsurance = parseFloat(renterInsuranceStr) || 0

    interface YearRow {
      year: number
      homeValue: number
      netBuyingCost: number
      netRentingCost: number
    }

    const yearData: YearRow[] = []
    let cumulativeMortgageAndCosts = 0
    let cumulativeRentAndCosts = 0
    let currentMonthlyRent = monthlyRent

    for (let y = 1; y <= timeHorizon; y++) {
      // Buying annual outflows
      const annualMortgage = monthlyMortgage * 12
      const annualTax = purchasePrice * propertyTaxRate
      const annualInsuranceCost = annualInsurance
      const annualHOA = monthlyHOA * 12

      cumulativeMortgageAndCosts += annualMortgage + annualTax + annualInsuranceCost + annualHOA

      const homeValue = purchasePrice * Math.pow(1 + appreciation / 100, y)
      const balance = remainingMortgageBalance(loanAmount, monthlyRate, nPayments, y)
      const saleNet = Math.max(0, homeValue * (1 - sellingCosts / 100) - balance)
      const netBuyingCost = downPayment + cumulativeMortgageAndCosts - saleNet

      // Renting annual outflows
      const annualRent = currentMonthlyRent * 12
      const annualRenterIns = renterInsurance * 12
      cumulativeRentAndCosts += annualRent + annualRenterIns
      currentMonthlyRent *= (1 + rentIncrease / 100)

      const oppCost = downPayment * (Math.pow(1 + investmentReturn / 100, y) - 1)
      const netRentingCost = cumulativeRentAndCosts + oppCost

      yearData.push({ year: y, homeValue, netBuyingCost, netRentingCost })
    }

    const finalYear = yearData[yearData.length - 1]
    const buyingWins = finalYear.netBuyingCost <= finalYear.netRentingCost
    const diff = Math.abs(finalYear.netBuyingCost - finalYear.netRentingCost)

    const breakEvenYear = yearData.find(d => d.netBuyingCost <= d.netRentingCost)?.year ?? null

    // Chart sample points: years 5, 10, 15, and horizon (only unique ones)
    const chartPoints = [5, 10, 15, timeHorizon]
      .filter((y, i, arr) => y <= timeHorizon && arr.indexOf(y) === i)
      .map(y => yearData[y - 1])
      .filter(Boolean)

    const maxCost = Math.max(...chartPoints.map(p => Math.max(p.netBuyingCost, p.netRentingCost)))

    return { yearData, finalYear, buyingWins, diff, breakEvenYear, chartPoints, maxCost, monthlyMortgage, downPayment }
  }, [
    purchasePriceStr, monthlyRentStr, downPaymentPct, mortgageRate, loanTerm,
    propertyTaxRateStr, insuranceStr, hoaStr, appreciation, sellingCosts,
    rentIncrease, renterInsuranceStr, timeHorizon, investmentReturn,
  ])

  const fmt = (n: number) => `${currency}${Math.round(Math.abs(n)).toLocaleString()}`

  const tabs = [
    { id: "buying" as const, label: "Buying" },
    { id: "renting" as const, label: "Renting" },
    { id: "assumptions" as const, label: "Assumptions" },
  ]

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6 space-y-6">

          {/* Tab Navigation */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-slate-50 w-full">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? "bg-primary text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {/* Input Panels */}
            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">

              {activeTab === "buying" && (
                <>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Buying Inputs</p>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Home Purchase Price</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                      <input type="number" min="0" value={purchasePriceStr} onChange={e => setPurchasePriceStr(e.target.value)} placeholder="e.g. 400000" className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Down Payment: {downPaymentPct}%</label>
                    <input type="range" min="5" max="50" step="1" value={downPaymentPct} onChange={e => setDownPaymentPct(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>5%</span><span>50%</span></div>
                    {parseFloat(purchasePriceStr) > 0 && (
                      <p className="text-xs text-primary font-medium mt-1">{fmt(parseFloat(purchasePriceStr) * downPaymentPct / 100)} down</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Mortgage Rate: {mortgageRate}%</label>
                    <input type="range" min="1" max="10" step="0.1" value={mortgageRate} onChange={e => setMortgageRate(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>1%</span><span>10%</span></div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Loan Term</label>
                    <div className="flex gap-2">
                      {[25, 30].map(t => (
                        <button key={t} onClick={() => setLoanTerm(t)} className={`flex-1 py-1.5 text-sm rounded-lg border font-medium transition-colors ${loanTerm === t ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
                          {t} yr
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Property Tax Rate: {propertyTaxRateStr}%</label>
                    <div className="relative">
                      <input type="number" min="0" step="0.1" value={propertyTaxRateStr} onChange={e => setPropertyTaxRateStr(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Home Insurance (annual)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                      <input type="number" min="0" value={insuranceStr} onChange={e => setInsuranceStr(e.target.value)} placeholder="e.g. 1200" className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">HOA / Maintenance (monthly)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                      <input type="number" min="0" value={hoaStr} onChange={e => setHoaStr(e.target.value)} placeholder="e.g. 200" className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Annual Appreciation: {appreciation}%</label>
                    <input type="range" min="0" max="10" step="0.5" value={appreciation} onChange={e => setAppreciation(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>0%</span><span>10%</span></div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Selling Costs: {sellingCosts}%</label>
                    <input type="range" min="3" max="10" step="0.5" value={sellingCosts} onChange={e => setSellingCosts(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>3%</span><span>10%</span></div>
                  </div>
                </>
              )}

              {activeTab === "renting" && (
                <>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Renting Inputs</p>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Monthly Rent</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                      <input type="number" min="0" value={monthlyRentStr} onChange={e => setMonthlyRentStr(e.target.value)} placeholder="e.g. 1800" className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Annual Rent Increase: {rentIncrease}%</label>
                    <input type="range" min="0" max="8" step="0.5" value={rentIncrease} onChange={e => setRentIncrease(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>0%</span><span>8%</span></div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Renter's Insurance (monthly)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                      <input type="number" min="0" value={renterInsuranceStr} onChange={e => setRenterInsuranceStr(e.target.value)} placeholder="e.g. 20" className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "assumptions" && (
                <>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Assumptions</p>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Time Horizon: {timeHorizon} years</label>
                    <input type="range" min="1" max="30" step="1" value={timeHorizon} onChange={e => setTimeHorizon(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>1yr</span><span>30yr</span></div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Investment Return on Down Payment: {investmentReturn}%</label>
                    <input type="range" min="3" max="12" step="0.5" value={investmentReturn} onChange={e => setInvestmentReturn(Number(e.target.value))} className="w-full accent-primary" />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5"><span>3%</span><span>12%</span></div>
                    <p className="text-xs text-slate-400 mt-1">Opportunity cost — what renters could earn by investing the down payment</p>
                  </div>
                </>
              )}
            </div>

            {/* Results Panel */}
            <div className="md:col-span-3 space-y-4">
              {!results ? (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
                  <p className="text-slate-400 text-sm text-center py-10">Enter a purchase price and monthly rent to compare</p>
                </div>
              ) : (
                <>
                  {/* Winner Banner */}
                  <div className={`rounded-2xl p-5 text-center ${results.buyingWins ? "bg-green-50 border border-green-200" : "bg-blue-50 border border-blue-200"}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1 text-slate-500">
                      Over {timeHorizon} years
                    </p>
                    <p className={`text-2xl font-extrabold ${results.buyingWins ? "text-green-700" : "text-blue-700"}`}>
                      {results.buyingWins ? "Buying" : "Renting"} is cheaper by
                    </p>
                    <p className={`text-4xl font-extrabold mt-1 ${results.buyingWins ? "text-green-600" : "text-blue-600"}`}>
                      {fmt(results.diff)}
                    </p>
                    {results.breakEvenYear ? (
                      <p className="text-sm text-slate-600 mt-2">
                        Buying becomes cheaper after <strong>Year {results.breakEvenYear}</strong>
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">Renting cheaper throughout the {timeHorizon}-year horizon</p>
                    )}
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Net Buying Cost</p>
                      <p className="text-2xl font-extrabold text-slate-900">{fmt(results.finalYear.netBuyingCost)}</p>
                      <p className="text-xs text-slate-500 mt-1">{fmt(results.monthlyMortgage)}/mo mortgage</p>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Net Renting Cost</p>
                      <p className="text-2xl font-extrabold text-slate-900">{fmt(results.finalYear.netRentingCost)}</p>
                      <p className="text-xs text-slate-500 mt-1">incl. opportunity cost</p>
                    </div>
                  </div>

                  {/* CSS Bar Chart */}
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">Cumulative Cost Comparison</p>
                    <div className="space-y-4">
                      {results.chartPoints.map((point) => {
                        const buyPct = results.maxCost > 0 ? (point.netBuyingCost / results.maxCost) * 100 : 0
                        const rentPct = results.maxCost > 0 ? (point.netRentingCost / results.maxCost) * 100 : 0
                        return (
                          <div key={point.year} className="space-y-1.5">
                            <p className="text-xs font-semibold text-slate-600">Year {point.year}</p>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-10 flex-shrink-0">Buy</span>
                                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-green-400 rounded-full flex items-center justify-end pr-1.5 transition-all" style={{ width: `${Math.max(buyPct, 1)}%` }}>
                                    <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmt(point.netBuyingCost)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-10 flex-shrink-0">Rent</span>
                                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-400 rounded-full flex items-center justify-end pr-1.5 transition-all" style={{ width: `${Math.max(rentPct, 1)}%` }}>
                                    <span className="text-[10px] font-bold text-white whitespace-nowrap">{fmt(point.netRentingCost)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-400" /><span className="text-xs text-slate-500">Buying</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-400" /><span className="text-xs text-slate-500">Renting (incl. opp. cost)</span></div>
                    </div>
                  </div>

                  {/* Year-by-Year Table */}
                  <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                    <button
                      onClick={() => setShowYearTable(!showYearTable)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-sm font-semibold text-slate-700"
                    >
                      <span>Year-by-Year Breakdown</span>
                      {showYearTable ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                    </button>
                    {showYearTable && (
                      <div className="border-t border-slate-100 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr className="text-slate-500">
                              <th className="text-left px-4 py-2 font-medium">Year</th>
                              <th className="text-right px-4 py-2 font-medium">Home Value</th>
                              <th className="text-right px-4 py-2 font-medium">Net Buying</th>
                              <th className="text-right px-4 py-2 font-medium">Net Renting</th>
                              <th className="text-right px-4 py-2 font-medium">Advantage</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {results.yearData.map((row) => {
                              const buyWins = row.netBuyingCost <= row.netRentingCost
                              const diff = Math.abs(row.netBuyingCost - row.netRentingCost)
                              return (
                                <tr key={row.year} className="text-slate-700 hover:bg-slate-50/50">
                                  <td className="px-4 py-1.5 font-medium">{row.year}</td>
                                  <td className="text-right px-4 py-1.5">{fmt(row.homeValue)}</td>
                                  <td className="text-right px-4 py-1.5">{fmt(row.netBuyingCost)}</td>
                                  <td className="text-right px-4 py-1.5">{fmt(row.netRentingCost)}</td>
                                  <td className={`text-right px-4 py-1.5 font-semibold ${buyWins ? "text-green-600" : "text-blue-600"}`}>
                                    {buyWins ? "Buy" : "Rent"} +{fmt(diff)}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-amber-800 mb-1">Important Assumptions</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              This model makes significant simplifications. Tax deductions (mortgage interest, property tax), maintenance variability, local market conditions, lifestyle factors, and life changes are not fully captured. Property appreciation and investment returns are not guaranteed. Use this as a starting point for discussion, not a final financial decision.
            </p>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Net Buying Cost = Down Payment + Σ (Annual Mortgage + Tax + Insurance + HOA) − Sale Equity\nSale Equity = Home Value × (1 − Selling Costs%) − Remaining Mortgage Balance\n\nNet Renting Cost = Σ (Annual Rent × Compounded Increases + Renter Insurance) + Opportunity Cost\nOpportunity Cost = Down Payment × ((1 + Investment Return%)^Years − 1)\n\nBreak-Even Year = First year where Net Buying Cost ≤ Net Renting Cost`}
        description="The model compares net cumulative costs rather than raw payments. For buyers, equity built through appreciation and mortgage paydown reduces total cost. For renters, the opportunity cost of the down payment not invested is added."
      />

      <ToolFAQ
        items={[
          {
            q: "What is the opportunity cost in this calculator?",
            a: "When you buy, your down payment is locked into the property. If you had rented instead, you could have invested that down payment in the stock market or other assets. The opportunity cost models the potential growth of that down payment had it been invested — making the renting comparison fairer.",
          },
          {
            q: "Why does buying often look cheaper in the long run?",
            a: "As years pass, property appreciation builds equity that offsets your accumulated mortgage costs. Meanwhile, rent typically increases each year, making renting cumulatively more expensive. The break-even year is when the accumulated equity advantage of buying outweighs all the extra costs of ownership.",
          },
          {
            q: "Should I buy if I might move in the next 2–3 years?",
            a: "Generally no. The upfront costs of buying (stamp duty, legal fees, selling costs) are significant. If you sell within 2–3 years, you're unlikely to have built enough equity to offset these costs. Short-term buyers often find renting is more financially sensible.",
          },
        ]}
      />

      <CTABox
        icon={Home}
        heading="Ready to make the move from renting?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
